import type { CommentRecord } from "@shared/types";
import { useEffect, useMemo, useRef, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { Eyebrow, FloatPanel, StudioButton } from "@/features/studio/theme";
import type { BaseCommentSubjectAdapter } from "../comment-subject-adapter";
import { CommentsApiClient } from "../comments-api-client";
import { CommentsComposerController } from "../comments-composer-controller";
import { CommentsUploadClient } from "../comments-upload-client";
import { useCommentsStore } from "../store/comments.store";
import type { CommentDto } from "../types";
import { CommentComposer } from "./comment-composer";
import { CommentThread } from "./comment-thread";
import { CommentsRealtimeBridge } from "./comments-realtime-bridge";

function recordToDto(
	record: {
		id: string;
		projectId: string;
		userId: string;
		content: string;
		fileUrl: string | null;
		createdAt: string;
		updatedAt: string | null;
		authorName?: string;
		authorAvatar?: string;
	},
	subjectId: string,
): CommentDto {
	const ext = record.fileUrl?.split(".").pop()?.toLowerCase() ?? "";
	const isPdf = ext === "pdf";
	return {
		id: record.id,
		subjectType: "project",
		subjectId,
		content: record.content,
		author: {
			userId: record.userId,
			userType: null,
			displayName: record.authorName ?? "Unknown user",
			profilePictureUrl: record.authorAvatar ?? null,
		},
		attachment: record.fileUrl
			? {
					url: record.fileUrl,
					filename: record.fileUrl.split("/").pop() ?? "attachment",
					mimeType: isPdf ? "application/pdf" : "image/png",
					size: 0,
					kind: isPdf ? "pdf" : "image",
				}
			: null,
		createdAt: record.createdAt,
		updatedAt: record.updatedAt ?? record.createdAt,
	};
}

export function CommentsPanel({
	subjectId,
	adapter,
	apiClient: apiClientProp,
	uploadClient: uploadClientProp,
	composerController: composerControllerProp,
}: {
	subjectId: string;
	adapter: BaseCommentSubjectAdapter;
	apiClient?: Pick<CommentsApiClient, "list" | "create">;
	uploadClient?: Pick<CommentsUploadClient, "upload">;
	composerController?: Pick<CommentsComposerController, "submit">;
}): React.ReactElement {
	const stableApiClient = useRef<Pick<
		CommentsApiClient,
		"list" | "create"
	> | null>(null);
	const stableUploadClient = useRef<Pick<
		CommentsUploadClient,
		"upload"
	> | null>(null);

	stableApiClient.current ??= apiClientProp ?? new CommentsApiClient();
	stableUploadClient.current ??=
		uploadClientProp ?? new CommentsUploadClient(subjectId);
	const apiClient = stableApiClient.current;
	const uploadClient = stableUploadClient.current;

	const controller = useMemo(
		() =>
			composerControllerProp ??
			new CommentsComposerController(apiClient, uploadClient),
		[apiClient, composerControllerProp, uploadClient],
	);
	const subjectRef = useMemo(
		() => adapter.getSubjectRef(subjectId),
		[adapter, subjectId],
	);

	const [comments, setComments] = useState<CommentDto[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const storeCommentsRef = useRef<CommentRecord[]>([]);

	const setProjectId = useCommentsStore((s) => s.setProjectId);
	const initFromDto = useCommentsStore((s) => s.initFromDto);
	const appendRealtime = useCommentsStore((s) => s.appendRealtime);

	useEffect(() => {
		setProjectId(subjectId);
	}, [subjectId, setProjectId]);

	useEffect(() => {
		void (async () => {
			setIsLoading(true);
			setError(null);
			try {
				const result = await apiClient.list(subjectRef);
				setComments(result);
				const asRecords: CommentRecord[] = result.map((c) => ({
					id: c.id,
					projectId: c.subjectId,
					userId: c.author.userId,
					content: c.content,
					fileUrl: c.attachment?.url ?? null,
					createdAt: c.createdAt,
					updatedAt: c.updatedAt,
					authorName: c.author.displayName,
					authorAvatar: c.author.profilePictureUrl ?? undefined,
				}));
				storeCommentsRef.current = asRecords;
				void initFromDto(asRecords);
			} catch {
				setError("Failed to load comments. Please try again.");
			} finally {
				setIsLoading(false);
			}
		})();
		// Only re-fetch when the subject changes.
		// setProjectId / initFromDto are stable zustand selectors.
	}, [apiClient, subjectRef, initFromDto]);

	useEffect(() => {
		let prevLength = storeCommentsRef.current.length;
		const unsubscribe = useCommentsStore.subscribe((state) => {
			const current = state.comments;
			if (current.length > prevLength) {
				const newRecords = current.slice(prevLength);
				prevLength = current.length;
				const newDtos = newRecords.map((r) => recordToDto(r, subjectId));
				setComments((prev) => {
					const existingIds = new Set(prev.map((c) => c.id));
					const fresh = newDtos.filter((d) => !existingIds.has(d.id));
					return [...prev, ...fresh];
				});
				storeCommentsRef.current = useCommentsStore.getState().comments;
			}
		});
		return unsubscribe;
	}, [subjectId]);

	const handleSubmit = async (draft: {
		content: string;
		file: File | null;
	}) => {
		setIsSubmitting(true);
		try {
			const result = await controller.submit(subjectRef, draft);
			if (result.success && result.comment) {
				const newComment = result.comment;
				setComments((current) => [...current, newComment]);
				void appendRealtime({
					id: newComment.id,
					projectId: newComment.subjectId,
					userId: newComment.author.userId,
					content: newComment.content,
					fileUrl: newComment.attachment?.url ?? null,
					createdAt: newComment.createdAt,
					updatedAt: newComment.updatedAt,
					authorName: newComment.author.displayName,
					authorAvatar: newComment.author.profilePictureUrl ?? undefined,
				});
				return { success: true };
			}
			return { success: false, error: result.error };
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<>
			<CommentsRealtimeBridge projectId={subjectId} />
			{isLoading ? (
				<div className="space-y-6">
					<div className="flex justify-between items-end flex-wrap gap-4">
						<div>
							<Eyebrow style={{ fontSize: 9 }}>COMMENTS · LOADING</Eyebrow>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 34,
									lineHeight: 1.05,
									letterSpacing: "-0.02em",
									color: "var(--color-dashboard-text)",
									margin: "8px 0 0",
								}}
							>
								Thoughts &amp;{" "}
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										color: "var(--color-dashboard-accent-display)",
									}}
								>
									collaboration
								</span>
								.
							</h2>
						</div>
					</div>
					<div className="space-y-3">
						<Skeleton className="h-20 w-full" />
						<Skeleton className="h-20 w-full" />
					</div>
				</div>
			) : error ? (
				<div className="space-y-6">
					<div className="flex justify-between items-end flex-wrap gap-4">
						<div>
							<Eyebrow style={{ fontSize: 9 }}>ERROR LOADING COMMENTS</Eyebrow>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 34,
									lineHeight: 1.05,
									letterSpacing: "-0.02em",
									color: "var(--color-dashboard-text)",
									margin: "8px 0 0",
								}}
							>
								Thoughts &amp;{" "}
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										color: "var(--color-dashboard-accent-display)",
									}}
								>
									collaboration
								</span>
								.
							</h2>
						</div>
					</div>
					<FloatPanel elevation={1} padding={24}>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 13.5,
								color: "var(--color-dashboard-pill-risk)",
							}}
						>
							{error}
						</p>
						<StudioButton
							type="button"
							variant="secondary"
							size="md"
							onClick={() => window.location.reload()}
							style={{ marginTop: 16 }}
						>
							Retry
						</StudioButton>
					</FloatPanel>
				</div>
			) : (
				<div className="space-y-6">
					<div className="flex justify-between items-end flex-wrap gap-4">
						<div>
							<Eyebrow style={{ fontSize: 9 }}>
								COMMENTS · {comments.length} ITEMS
							</Eyebrow>
							<h2
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 500,
									fontSize: 34,
									lineHeight: 1.05,
									letterSpacing: "-0.02em",
									color: "var(--color-dashboard-text)",
									margin: "8px 0 0",
								}}
							>
								Thoughts &amp;{" "}
								<span
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										color: "var(--color-dashboard-accent-display)",
									}}
								>
									collaboration
								</span>
								.
							</h2>
						</div>
					</div>

					{comments.length === 0 ? (
						<FloatPanel elevation={1} padding={32}>
							<div className="py-4 text-center">
								<p
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										fontSize: 16.5,
										fontWeight: 600,
										color: "var(--color-dashboard-text)",
									}}
								>
									{adapter.getEmptyStateTitle()}
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 13,
										color: "var(--color-dashboard-muted)",
										marginTop: 6,
									}}
								>
									{adapter.getEmptyStateDescription()}
								</p>
							</div>
						</FloatPanel>
					) : (
						<CommentThread comments={comments} />
					)}

					<CommentComposer
						onSubmit={handleSubmit}
						isSubmitting={isSubmitting}
					/>
				</div>
			)}
		</>
	);
}
